//@flow
import React, { Component } from 'react';
import { findDOMNode } from 'react-dom';
import { createFragmentContainer, graphql } from 'react-relay';
import getter from 'dibs-object-getter';

import { logError } from '../../helpers';
import { ItemTag } from './ItemTag';

import styles from './ItemTagOverlay.scss';

import type { ItemTagOverlay_tradePhoto as TradePhoto } from './__generated__/ItemTagOverlay_tradePhoto.graphql';

type ItemTagIndicatorOverlay = {
    tradePhoto: ?TradePhoto,
    photoRef: ?any,
    containerRef: ?Element,
};

type ItemTagIndicatorOverlayState = {
    hovered: ?string,
    height: number,
    width: number,
};

export class ItemTagOverlayComponent extends Component<
    ItemTagIndicatorOverlay,
    ItemTagIndicatorOverlayState
> {
    constructor() {
        super();

        this.state = {
            hovered: null,
            height: 0,
            width: 0,
        };
    }

    componentDidMount = () => {
        this.updateImageSize();
        // istanbul ignore next
        window.addEventListener('onresize', this.updateImageSize);
        // istanbul ignore if
        if (this.props.photoRef && this.props.photoRef.current) {
            const imageDOMNode = findDOMNode(this.props.photoRef.current);
            // istanbul ignore if
            if (imageDOMNode && imageDOMNode instanceof HTMLElement) {
                imageDOMNode.addEventListener('load', this.updateImageSize);
            }
        }
    };

    componentWillUnmount = () => {
        // istanbul ignore next
        window.removeEventListener('onresize', this.updateImageSize);
    };

    updateImageSize = () => {
        // istanbul ignore if
        if (this.props.photoRef && this.props.photoRef.current) {
            const imageDOMNode = findDOMNode(this.props.photoRef.current);
            if (imageDOMNode && imageDOMNode instanceof HTMLElement) {
                const boundingClientRect = imageDOMNode.getBoundingClientRect() || {};

                let { height, width } = boundingClientRect;

                height = Math.floor(height);
                width = Math.floor(width);

                this.setState({ height, width });
            } else {
                logError('Could not find a DOM node for tag overlay');
            }
        }
    };

    onHover = (tagId: string): void => {
        this.setState({ hovered: tagId });
    };

    onCloseHover = (): void => {
        this.setState({ hovered: null });
    };

    render() {
        // do not display if the image has no height/width
        const { height, width } = this.state;
        if (!height || !width) {
            return null;
        }

        const photoTags =
            getter(this.props)
                .at('tradePhoto')
                .value('tags') || [];

        const displayTags = photoTags.map((tag, index) => {
            const tagId = getter(tag).value('serviceId');

            if (!tagId) {
                return null;
            }

            return (
                <ItemTag
                    data-tn="item-tag-element"
                    isHovered={tagId === this.state.hovered}
                    tag={(tag: ?Object)}
                    key={`photo-tag-${index}`}
                    onHover={this.onHover}
                    onClose={this.onCloseHover}
                    containerRef={this.props.containerRef}
                />
            );
        });

        return (
            <div className={styles.wrapper} style={{ height, width }} data-tn="item-tag-overlay">
                {displayTags}
            </div>
        );
    }
}

export const ItemTagOverlay = createFragmentContainer(ItemTagOverlayComponent, {
    tradePhoto: graphql`
        fragment ItemTagOverlay_tradePhoto on TradePhoto {
            tags {
                ...ItemTag_tag
                serviceId
            }
        }
    `,
});
